/**
 * Formats API errors and HTTP status codes into user-friendly messages for toasts and alerts.
 * Handles standard HTTP statuses (504, 502, 503, 500, 422, 409, etc.),
 * network timeouts, and raw string/HTML gateway responses.
 */
export const formatApiErrorMessage = (
  error,
  defaultMsg = "Failed to process request. Please try again."
) => {
  if (!error) return defaultMsg;

  // 1. Extract status code
  const status =
    error.response?.status ||
    error.status ||
    error.status_code ||
    error.statusCode ||
    (typeof error === "object" && error.code && !isNaN(Number(error.code))
      ? Number(error.code)
      : null);

  // 2. Map standard HTTP status codes
  if (status === 504) {
    return "Server is taking too long to respond. Please try again later.";
  }
  if (status === 502) {
    return "Server is temporarily unavailable (Bad Gateway 502). Please try again later.";
  }
  if (status === 503) {
    return "Server is temporarily unavailable (Service Unavailable 503). Please try again in a few moments.";
  }
  if (status === 500) {
    return "Internal server error (500). Please try again later or contact support.";
  }
  if (status === 408) {
    return "Request timeout (408). Please try again.";
  }
  if (status === 401) {
    return "Session expired. Please log in again.";
  }
  if (status === 403) {
    return "You do not have permission to perform this action.";
  }
  if (status === 404) {
    return "Requested resource not found (404).";
  }
  if (status === 409) {
    return (
      error.response?.data?.message ||
      error.message ||
      "A record with these details already exists."
    );
  }
  if (status === 422) {
    const validationErrors = error.response?.data?.errors;
    if (validationErrors && typeof validationErrors === "object") {
      const firstKey = Object.keys(validationErrors)[0];
      const firstError = validationErrors[firstKey];
      if (Array.isArray(firstError) && firstError[0]) {
        return firstError[0];
      }
      if (typeof firstError === "string") {
        return firstError;
      }
    }
    return (
      error.response?.data?.message ||
      error.message ||
      "Validation failed. Please verify your inputs."
    );
  }

  // 3. Axios timeout / network errors
  if (
    error.code === "ECONNABORTED" ||
    (typeof error.message === "string" &&
      error.message.toLowerCase().includes("timeout"))
  ) {
    return "Server is taking too long to respond. Please try again later.";
  }
  if (error.message === "Network Error") {
    return "Network error. Please check your internet connection.";
  }

  // 4. Handle raw string errors (e.g. HTML 504 from Nginx)
  if (typeof error === "string") {
    const lower = error.toLowerCase();
    if (
      lower.includes("504") ||
      lower.includes("gateway time-out") ||
      lower.includes("gateway timeout")
    ) {
      return "Server is taking too long to respond. Please try again later.";
    }
    if (lower.includes("502") || lower.includes("bad gateway")) {
      return "Server is temporarily unavailable. Please try again later.";
    }
    if (lower.includes("503") || lower.includes("service unavailable")) {
      return "Server is temporarily unavailable. Please try again later.";
    }
    if (lower.includes("500") || lower.includes("internal server error")) {
      return "Internal server error. Please try again later.";
    }
    if (error.trim().startsWith("<") && error.trim().endsWith(">")) {
      return "Server returned an error. Please try again later.";
    }
    return error;
  }

  // 5. Backend JSON response message
  const msg =
    error.response?.data?.message || error.data?.message || error.message;
  if (typeof msg === "string" && msg.trim()) {
    const lowerMsg = msg.toLowerCase();
    if (
      lowerMsg.includes("504") ||
      lowerMsg.includes("gateway timeout") ||
      lowerMsg.includes("gateway time-out")
    ) {
      return "Server is taking too long to respond. Please try again later.";
    }
    return msg;
  }

  return defaultMsg;
};

export default formatApiErrorMessage;
